/**
 * Main canvas map renderer that orchestrates tile fetching, MGRS grid drawing,
 * and military symbol rendering.
 */

import { RenderOptions, ProgressCallback, RenderBounds } from './types';
import { CoordinateUtils, calculateZoomForBounds } from './CoordinateUtils';
import { TileFetcher } from './TileFetcher';
import { SymbolRenderer } from './SymbolRenderer';
import { MgrsGridDrawer } from './MgrsGridDrawer';
import { llToMgrs } from './MgrsCoordinates';

export class CanvasMapRenderer {
    private tileFetcher: TileFetcher;
    private symbolRenderer: SymbolRenderer;
    private mgrsGridDrawer: MgrsGridDrawer;

    constructor() {
        this.tileFetcher = new TileFetcher();
        this.symbolRenderer = new SymbolRenderer();
        this.mgrsGridDrawer = new MgrsGridDrawer();
    }

    /**
     * Render a map to a canvas element.
     *
     * @param options - Render options including bounds, dimensions, and entities
     * @param onProgress - Optional progress callback
     * @returns The rendered canvas element
     */
    async render(options: RenderOptions, onProgress?: ProgressCallback): Promise<HTMLCanvasElement> {
        const { bounds, widthPx, heightPx, entities, showMgrsGrid, tileUrl, symbolSize = 32, gridInterval } = options;

        // Calculate the appropriate zoom level for the given bounds and output size
        const zoom = calculateZoomForBounds(bounds, widthPx, heightPx);

        // Initialize coordinate utilities
        const coordUtils = new CoordinateUtils(bounds, widthPx, heightPx, zoom);

        // Create the output canvas
        const canvas = document.createElement('canvas');
        canvas.width = widthPx;
        canvas.height = heightPx;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            throw new Error('Could not get canvas 2D context');
        }

        // Fill with white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, widthPx, heightPx);

        // Phase 1: Fetch and draw tiles
        onProgress?.({ phase: 'tiles', tilesLoaded: 0, tilesTotal: 0, percentage: 0 });
        await this.renderTiles(ctx, coordUtils, tileUrl, onProgress);

        // Phase 2: Draw MGRS grid if enabled
        if (showMgrsGrid) {
            onProgress?.({ phase: 'grid', tilesLoaded: 0, tilesTotal: 0, percentage: 60 });
            this.mgrsGridDrawer.draw(ctx, coordUtils, gridInterval);

            // Draw zone/grid info in bottom right corner
            this.drawGridInfo(ctx, bounds, widthPx, heightPx);
        }

        // Phase 3: Draw military symbols
        onProgress?.({ phase: 'markers', tilesLoaded: 0, tilesTotal: 0, percentage: 80 });
        await this.symbolRenderer.drawEntities(ctx, entities, coordUtils, symbolSize);

        // Complete
        onProgress?.({ phase: 'complete', tilesLoaded: 0, tilesTotal: 0, percentage: 100 });

        return canvas;
    }

    /**
     * Fetch and render map tiles.
     */
    private async renderTiles(
        ctx: CanvasRenderingContext2D,
        coordUtils: CoordinateUtils,
        tileUrl: string,
        onProgress?: ProgressCallback
    ): Promise<void> {
        // Get the list of tiles needed
        const tiles = coordUtils.getTileCoverage();

        // Fetch all tiles with progress tracking
        const tileImages = await this.tileFetcher.fetchTiles(
            tiles,
            tileUrl,
            (loaded, total) => {
                onProgress?.({
                    phase: 'tiles',
                    tilesLoaded: loaded,
                    tilesTotal: total,
                    percentage: Math.round((loaded / total) * 50), // Tiles are 0-50% of progress
                });
            }
        );

        // Calculate tile size on canvas
        const tileSize = coordUtils.getTileSizeOnCanvas();

        // Draw each tile
        for (const tile of tiles) {
            const key = `${tile.z}/${tile.x}/${tile.y}`;
            const img = tileImages.get(key);

            if (img) {
                const position = coordUtils.tileToCanvasPosition(tile);
                ctx.drawImage(img, position.x, position.y, tileSize, tileSize);
            }
        }
    }

    /**
     * Draw MGRS zone and 100km grid info in the bottom right corner.
     */
    private drawGridInfo(
        ctx: CanvasRenderingContext2D,
        bounds: RenderBounds,
        widthPx: number,
        heightPx: number
    ): void {
        // Get center point of the map
        const centerLat = (bounds.north + bounds.south) / 2;
        const centerLng = (bounds.east + bounds.west) / 2;

        try {
            // Get MGRS coordinate for center point (precision 1 = 10km)
            const mgrs = llToMgrs([centerLng, centerLat], 1);

            // Parse MGRS: format is like "33UVP1234" where 33U is GZD, VP is 100km grid
            const match = mgrs.match(/^(\d{1,2}[A-Z])([A-Z]{2})/);
            if (!match) return;

            const gzd = match[1];  // e.g., "33U"
            const grid100km = match[2];  // e.g., "VP"

            const infoText = `Zone: ${gzd}; Grid: ${grid100km}`;

            // Style for the info box
            const padding = 10;
            const margin = 20;
            const fontSize = Math.max(16, Math.round(heightPx / 60)); // Scale font with canvas size

            ctx.font = `bold ${fontSize}px Arial, sans-serif`;
            const textMetrics = ctx.measureText(infoText);
            const textWidth = textMetrics.width;
            const textHeight = fontSize;

            // Position in bottom right corner
            const boxWidth = textWidth + padding * 2;
            const boxHeight = textHeight + padding * 2;
            const boxX = widthPx - boxWidth - margin;
            const boxY = heightPx - boxHeight - margin;

            // Draw semi-transparent background
            ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
            ctx.fillRect(boxX, boxY, boxWidth, boxHeight);

            // Draw border
            ctx.strokeStyle = '#333333';
            ctx.lineWidth = 2;
            ctx.setLineDash([]);
            ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

            // Draw text
            ctx.fillStyle = '#000000';
            ctx.textBaseline = 'middle';
            ctx.fillText(infoText, boxX + padding, boxY + boxHeight / 2);
        } catch (error) {
            // Silently fail if MGRS conversion fails (e.g., outside valid range)
            console.warn('Could not determine MGRS grid info:', error);
        }
    }

    /**
     * Convert canvas to a data URL.
     */
    canvasToDataUrl(canvas: HTMLCanvasElement, type: string = 'image/jpeg', quality: number = 0.92): string {
        return canvas.toDataURL(type, quality);
    }

    /**
     * Clear caches to free memory.
     */
    clearCaches(): void {
        this.tileFetcher.clearCache();
        this.symbolRenderer.clearCache();
    }
}
